import { readAcademicCurricula } from "./useAcademicCatalogue";
import { useEffect, useRef, useState } from "react";
import { academyRequest, createCommandSender } from "./academyApi";
import { checkIdentitySession } from "./identityClient";
import { useIdentity } from "./useIdentity";
import { ACADEMIC_CATALOGUE_CHANGED } from "./useAcademicCatalogue";
import type { AcademicContent, AcademicDefinition, AcademicReceipt } from "./generated/academic-catalogue";

/** Approved inputs inside the existing Institution Course action dialog. */
export function AcademicCourseFields(props: {
  action?: "submit" | "approve" | "request-changes" | "publish";
  record?: Record<string, unknown>;
  formId: string;
  onSaved: () => void;
  onBusy: (busy: boolean) => void;
}) {
  const account = useIdentity();
  const membership = account.access?.memberships.find(
    item => item.workspaceId === account.session?.workspace.id,
  );
  const context = `${account.session?.tenantId}:${account.session?.user.id}:${account.session?.workspace.id}`;
  if (!account.session || !membership?.institutionId)
    return <p role="alert">Load your current Institution membership before editing Courses.</p>;
  return (
    <CourseFields
      key={`${context}:${props.record?.id || "create"}:${props.action || "edit"}`}
      {...props}
      context={context}
      institutionId={membership.institutionId}
      ownerName={account.session.workspace.name}
      canManage={account.session.permissions.includes(
        props.action === "publish"
          ? "institution.curriculum.publish"
          : props.action === "approve" || props.action === "request-changes"
            ? "institution.curriculum.approve"
            : "institution.curriculum.manage",
      )}
    />
  );
}

function CourseFields({
  action,
  record,
  formId,
  onSaved,
  onBusy,
  context,
  institutionId,
  ownerName,
  canManage,
}: Parameters<typeof AcademicCourseFields>[0] & {
  context: string;
  institutionId: string;
  ownerName: string;
  canManage: boolean;
}) {
  const [title, setTitle] = useState(""),
    [code, setCode] = useState(String(record?.code || "")),
    [description, setDescription] = useState("");
  const [versionLabel, setVersionLabel] = useState(""),
    [credits, setCredits] = useState(""),
    [creditUnit, setCreditUnit] = useState("");
  const [reason, setReason] = useState(""),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(!!record),
    [busy, setBusy] = useState(false);
  const [basis, setBasis] = useState<AcademicContent>();
  const sender = useRef(createCommandSender({ headerOnly: true }));
  const submitting = useRef(false);
  const path = `academic-catalogue/course/${encodeURIComponent(String(record?.id || ""))}/curricula`;
  useEffect(() => {
    if (!record) return;
    const abort = new AbortController();
    void (async () => {
      try {
        const versions = await readAcademicCurricula("course", String(record.id), abort.signal);
        if (!action && versions.some(item => item.state === "in_review"))
          throw new Error("This Course is awaiting review. Its submitted version cannot be edited.");
        const correction = versions
          .slice()
          .sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt))[0];
        const version = action
          ? versions.find(
              item =>
                item.state ===
                  (action === "submit" ? "draft" : action === "publish" ? "approved" : "in_review") &&
                !item.publicationId,
            )
          : versions.find(item => item.state === "draft") ||
            (correction && ["changes_requested", "rejected"].includes(correction.state)
              ? correction
              : versions.find(item => item.isCurrent));
        if (!version) throw new Error("A current draft, correction or published Course version is required.");
        const content = await academyRequest<AcademicContent>(
          `${path}/${version.id}`,
          undefined,
          "GET",
          true,
          undefined,
          abort.signal,
        );
        if (abort.signal.aborted) return;
        setBasis(content);
        setTitle(content.title);
        setDescription(content.description || "");
        setVersionLabel(action || content.state === "draft" ? content.versionLabel : "");
        setCredits(content.definition.credits === undefined ? "" : String(content.definition.credits));
        setCreditUnit(content.definition.creditUnit || "");
      } catch (failure) {
        if (!abort.signal.aborted)
          setError(failure instanceof Error ? failure.message : "Course details could not be loaded.");
      } finally {
        if (!abort.signal.aborted) setLoading(false);
      }
    })();
    return () => abort.abort();
  }, [path, record, action]);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submitting.current || loading || !canManage || (record && !basis)) return;
    if ((credits === "") !== (creditUnit.trim() === "")) {
      setError("Enter both the credit value and its unit, or leave both empty.");
      return;
    }
    submitting.current = true;
    setBusy(true);
    onBusy(true);
    setError("");
    try {
      const session = await checkIdentitySession();
      if (`${session.tenantId}:${session.user.id}:${session.workspace.id}` !== context)
        throw new Error("Your workspace changed. Reload before saving this Course.");
      const definition: AcademicDefinition = {
        title: title.trim(),
        description: description.trim(),
        ...(basis
          ? {
              deliveryModes: basis.definition.deliveryModes,
              languages: basis.definition.languages,
              visibility: basis.definition.visibility,
            }
          : {}),
        ...(credits === "" ? {} : { credits: Number(credits), creditUnit: creditUnit.trim() }),
      };
      if (action && basis) {
        await sender.current<AcademicReceipt>(`${path}/${basis.id}/commands/${action}`, {
          expectedVersion: basis.version,
          reason,
        });
      } else if (!record) {
        await sender.current<AcademicReceipt>("academic-catalogue/courses/commands/create", {
          code,
          institutionId,
          versionLabel,
          definition,
          reason,
        });
      } else if (basis) {
        let target = basis.id,
          expectedVersion = basis.version;
        if (basis.state !== "draft") {
          const clone = await sender.current<AcademicReceipt>(`${path}/${basis.id}/commands/clone`, {
            versionLabel,
            expectedVersion: basis.version,
            expectedParentVersion: basis.parentVersion,
            reason,
          });
          target = clone.id;
          expectedVersion = clone.version;
        }
        await sender.current<AcademicReceipt>(`${path}/${target}/commands/update`, {
          definition,
          expectedVersion,
          reason,
        });
      }
      window.dispatchEvent(new Event(ACADEMIC_CATALOGUE_CHANGED));
      onSaved();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "The Course change could not be recorded.");
    } finally {
      submitting.current = false;
      setBusy(false);
      onBusy(false);
    }
  };
  return (
    <form id={formId} onSubmit={submit} className="form-fields-v2">
      {loading && <p role="status">Loading the current Course version…</p>}
      {error && (
        <p role="alert" className="full">
          {error}
        </p>
      )}
      {!canManage && (
        <p role="alert" className="full">
          Current Course management permission is required.
        </p>
      )}
      <div className="form-field-v2 full">
        <label htmlFor={`${formId}-title`}>Name / title</label>
        <input
          id={`${formId}-title`}
          required
          minLength={2}
          maxLength={240}
          value={title}
          readOnly={!!action}
          disabled={busy || loading}
          onChange={event => setTitle(event.target.value)}
        />
      </div>
      <div className="form-field-v2">
        <label htmlFor={`${formId}-code`}>Course code</label>
        <input
          id={`${formId}-code`}
          required
          pattern="[a-z0-9]+([._-][a-z0-9]+)*"
          maxLength={120}
          readOnly={!!record}
          value={code}
          onChange={event => setCode(event.target.value)}
          disabled={busy || loading}
        />
      </div>
      <div className="form-field-v2">
        <label htmlFor={`${formId}-version`}>Version label</label>
        <input
          id={`${formId}-version`}
          required
          maxLength={80}
          readOnly={!!action || basis?.state === "draft"}
          value={versionLabel}
          onChange={event => setVersionLabel(event.target.value)}
          placeholder={basis?.state === "approved" ? "Name the new draft version" : "e.g. v1"}
          disabled={busy || loading}
        />
      </div>
      <div className="form-field-v2">
        <label htmlFor={`${formId}-owner`}>Owner / scope</label>
        <input id={`${formId}-owner`} readOnly value={ownerName} />
      </div>
      <div className="form-field-v2">
        <label htmlFor={`${formId}-state`}>{action ? "Selected version status" : "Initial status"}</label>
        <input id={`${formId}-state`} readOnly value={action ? basis?.state || "Loading" : "Draft"} />
      </div>
      <div className="form-field-v2">
        <label htmlFor={`${formId}-credits`}>Credits (optional)</label>
        <input
          id={`${formId}-credits`}
          type="number"
          min={0}
          max={1000}
          step="any"
          readOnly={!!action}
          value={credits}
          onChange={event => setCredits(event.target.value)}
          disabled={busy || loading}
        />
      </div>
      <div className="form-field-v2">
        <label htmlFor={`${formId}-unit`}>Credit unit</label>
        <input
          id={`${formId}-unit`}
          required={credits !== ""}
          maxLength={80}
          readOnly={!!action}
          value={creditUnit}
          onChange={event => setCreditUnit(event.target.value)}
          disabled={busy || loading}
        />
      </div>
      <div className="form-field-v2 full">
        <label htmlFor={`${formId}-description`}>Description</label>
        <textarea
          id={`${formId}-description`}
          maxLength={4000}
          readOnly={!!action}
          value={description}
          onChange={event => setDescription(event.target.value)}
          disabled={busy || loading}
        />
      </div>
      <div className="form-field-v2 full">
        <label htmlFor={`${formId}-reason`}>Reason for change</label>
        <textarea
          id={`${formId}-reason`}
          required
          minLength={10}
          maxLength={2000}
          value={reason}
          onChange={event => setReason(event.target.value)}
          disabled={busy || loading}
        />
      </div>
    </form>
  );
}
